import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  User,
  Building2,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Landmark,
  FileText,
  AlertCircle,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_ALIADO = [
  { value: 'wedding_planner', label: 'Wedding Planner' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'hacienda', label: 'Hacienda' },
  { value: 'fotografo', label: 'Fotógrafo' },
  { value: 'organizador_eventos', label: 'Organizador de Eventos' },
  { value: 'productora', label: 'Productora' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'aliado_bodas', label: 'Aliado del Sector Bodas' },
  { value: 'otro', label: 'Otro' }
];

const TIPOS_DOCUMENTO = [
  { value: 'cc', label: 'Cédula de Ciudadanía' },
  { value: 'ce', label: 'Cédula de Extranjería' },
  { value: 'nit', label: 'NIT' },
  { value: 'pasaporte', label: 'Pasaporte' }
];

const TIPOS_CUENTA = [
  { value: 'ahorros', label: 'Ahorros' },
  { value: 'corriente', label: 'Corriente' }
];

const BANCOS = [
  'Bancolombia', 'Banco de Bogotá', 'Davivienda', 'BBVA', 
  'Banco de Occidente', 'Banco Popular', 'Banco AV Villas',
  'Banco Caja Social', 'Banco Falabella', 'Banco Pichincha',
  'Scotiabank Colpatria', 'Banco Agrario', 'Banco Itaú',
  'Nequi', 'Daviplata', 'Otro'
];

const AliadoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basicos');
  
  // Si el usuario es aliado, solo puede editar datos de cobro
  const isAliado = user?.role === 'aliado';
  
  const [formData, setFormData] = useState({
    nombre: '',
    tipo_aliado: 'aliado_bodas',
    tipo_documento: 'cc',
    numero_documento: '',
    telefono: '',
    whatsapp: '',
    correo: '',
    direccion: '',
    ciudad: '',
    estado: 'activo',
    observaciones: '',
    datos_cobro: {
      titular_cuenta: '',
      tipo_documento_titular: 'cc',
      documento_titular: '',
      banco: '',
      tipo_cuenta: 'ahorros',
      numero_cuenta: '',
      llave_pago: '',
      metodo_pago: '',
      telefono_pago: '',
      correo_pago: ''
    }
  });

  useEffect(() => {
    if (isEditing) {
      fetchAliado();
    }
  }, [id]);

  const fetchAliado = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({
        ...response.data,
        datos_cobro: response.data.datos_cobro || {
          titular_cuenta: '',
          tipo_documento_titular: 'cc',
          documento_titular: '',
          banco: '',
          tipo_cuenta: 'ahorros',
          numero_cuenta: '',
          llave_pago: '',
          metodo_pago: '',
          telefono_pago: '',
          correo_pago: ''
        }
      });
      // Si es aliado, ir directo a datos de cobro
      if (isAliado) {
        setActiveTab('cobro');
      }
    } catch (error) {
      toast.error('Error al cargar el aliado');
      navigate('/aliados');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('datos_cobro.')) {
      const field = name.replace('datos_cobro.', '');
      setFormData(prev => ({
        ...prev,
        datos_cobro: {
          ...prev.datos_cobro,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones (solo si no es aliado o si está creando)
    if (!isAliado || !isEditing) {
      if (!formData.nombre.trim()) {
        toast.error('El nombre es obligatorio');
        return;
      }
      if (!formData.numero_documento.trim()) {
        toast.error('El número de documento es obligatorio');
        return;
      }
      if (!formData.telefono.trim()) {
        toast.error('El teléfono es obligatorio');
        return;
      }
      if (!formData.correo.trim()) {
        toast.error('El correo es obligatorio');
        return;
      }
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Si es aliado, enviar datos de cobro + campos de contacto permitidos
      let payload;
      if (isAliado && isEditing) {
        payload = { 
          datos_cobro: formData.datos_cobro,
          // Campos de contacto que el aliado puede actualizar
          telefono: formData.telefono,
          whatsapp: formData.whatsapp,
          direccion: formData.direccion,
          ciudad: formData.ciudad,
          observaciones: formData.observaciones
        };
      } else {
        payload = formData;
      }
      
      if (isEditing) {
        await axios.put(`${API_URL}/api/aliados/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Aliado actualizado exitosamente');
      } else {
        await axios.post(`${API_URL}/api/aliados`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Aliado creado exitosamente');
      }
      
      // Navegar según el rol
      if (isAliado) {
        navigate('/panel-aliado');
      } else {
        navigate('/aliados');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el aliado');
    } finally {
      setSaving(false);
    }
  };

  // Verificar si datos bancarios están completos
  const datosBancariosCompletos = formData.datos_cobro.titular_cuenta && 
    formData.datos_cobro.documento_titular && 
    formData.datos_cobro.banco && 
    formData.datos_cobro.numero_cuenta;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-black border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => isAliado ? navigate('/panel-aliado') : navigate('/aliados')}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{isAliado ? 'Mi Panel' : 'Aliados'}</span>
              </button>
              <div className="h-6 w-px bg-zinc-800" />
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <h1 className="font-display text-lg">
                  {isEditing ? (isAliado ? 'Mis Datos' : 'Editar Aliado') : 'Nuevo Aliado'}
                </h1>
              </div>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2 rounded-lg hover:bg-[#C49F27] disabled:opacity-50 transition-colors font-medium"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">{saving ? 'Guardando...' : 'Guardar'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        {!isAliado && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            <button
              onClick={() => setActiveTab('basicos')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg whitespace-nowrap transition-all ${
                activeTab === 'basicos'
                  ? 'bg-[#D4AF37] text-black font-medium'
                  : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <User className="w-4 h-4" />
              Datos Básicos
            </button>
            <button
              onClick={() => setActiveTab('cobro')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg whitespace-nowrap transition-all ${
                activeTab === 'cobro'
                  ? 'bg-[#D4AF37] text-black font-medium'
                  : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <CreditCard className="w-4 h-4" />
              Datos de Cobro
              {datosBancariosCompletos && (
                <CheckCircle className="w-4 h-4 text-green-500" />
              )}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Tab: Datos Básicos */}
          {activeTab === 'basicos' && !isAliado && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-6">
              <h2 className="font-display text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-[#D4AF37]" />
                Información del Aliado
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nombre */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Nombre o Razón Social *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Nombre completo o razón social"
                    required
                  />
                </div>
                
                {/* Tipo de aliado */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tipo de Aliado *
                  </label>
                  <select
                    name="tipo_aliado"
                    value={formData.tipo_aliado}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    {TIPOS_ALIADO.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Estado
                  </label>
                  <select
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="suspendido">Suspendido</option>
                  </select>
                </div>
                
                {/* Tipo de documento */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tipo de Documento *
                  </label>
                  <select
                    name="tipo_documento"
                    value={formData.tipo_documento}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    {TIPOS_DOCUMENTO.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Número de documento */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Número de Documento *
                  </label>
                  <input
                    type="text"
                    name="numero_documento"
                    value={formData.numero_documento}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Número de documento"
                    required
                  />
                </div>
                
                {/* Teléfono */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="3001234567"
                    required
                  />
                </div>
                
                {/* WhatsApp */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="3001234567"
                  />
                </div>
                
                {/* Correo */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    name="correo"
                    value={formData.correo}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="correo@ejemplo.com"
                    required
                  />
                </div>
                
                {/* Ciudad */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Ciudad
                  </label>
                  <input
                    type="text"
                    name="ciudad"
                    value={formData.ciudad}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Ciudad"
                  />
                </div>
                
                {/* Dirección */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Dirección
                  </label>
                  <input
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Dirección"
                  />
                </div>
                
                {/* Observaciones */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Observaciones
                  </label>
                  <textarea
                    name="observaciones"
                    value={formData.observaciones}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white resize-none"
                    placeholder="Notas adicionales sobre el aliado..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Datos de Cobro */}
          {(activeTab === 'cobro' || isAliado) && (
            <div className="space-y-6">
              {/* Sección de información de contacto para aliados */}
              {isAliado && isEditing && (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Phone className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h2 className="font-display text-lg">Información de Contacto</h2>
                      <p className="text-sm text-zinc-400">
                        Actualiza tu información de contacto
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Información no editable */}
                    <div className="md:col-span-2 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                      <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">Datos registrados (no editables)</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-zinc-500">Nombre:</span>
                          <span className="ml-2 text-white">{formData.nombre}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">Documento:</span>
                          <span className="ml-2 text-white">{formData.numero_documento}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">Correo:</span>
                          <span className="ml-2 text-white">{formData.correo}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Teléfono */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                        placeholder="3001234567"
                      />
                    </div>
                    
                    {/* WhatsApp */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        WhatsApp
                      </label>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                        placeholder="3001234567"
                      />
                    </div>
                    
                    {/* Ciudad */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        Ciudad
                      </label>
                      <input
                        type="text"
                        name="ciudad"
                        value={formData.ciudad}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                        placeholder="Ej: Bogotá"
                      />
                    </div>
                    
                    {/* Dirección */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Dirección
                      </label>
                      <input
                        type="text"
                        name="direccion"
                        value={formData.direccion}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                        placeholder="Dirección de contacto"
                      />
                    </div>
                    
                    {/* Observaciones */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-2">
                        Notas adicionales
                      </label>
                      <textarea
                        name="observaciones"
                        value={formData.observaciones}
                        onChange={handleChange}
                        rows={2}
                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white resize-none"
                        placeholder="Información adicional..."
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Datos Bancarios */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-[#D4AF37]/20 rounded-lg">
                  <Landmark className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="font-display text-lg">Datos Bancarios para Cobro</h2>
                  <p className="text-sm text-zinc-400">
                    Estos datos se usarán para generar las cuentas de cobro de comisiones
                  </p>
                </div>
              </div>
              
              {/* Alerta de datos incompletos */}
              {!datosBancariosCompletos && (
                <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <p className="text-sm text-amber-200">
                    Complete los datos bancarios para poder generar cuentas de cobro
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Titular de la cuenta */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Nombre del Titular de la Cuenta *
                  </label>
                  <input
                    type="text"
                    name="datos_cobro.titular_cuenta"
                    value={formData.datos_cobro.titular_cuenta}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Nombre completo del titular"
                  />
                </div>
                
                {/* Tipo de documento titular */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tipo de Documento del Titular
                  </label>
                  <select
                    name="datos_cobro.tipo_documento_titular"
                    value={formData.datos_cobro.tipo_documento_titular}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    {TIPOS_DOCUMENTO.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Documento titular */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Número de Documento del Titular *
                  </label>
                  <input
                    type="text"
                    name="datos_cobro.documento_titular"
                    value={formData.datos_cobro.documento_titular}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Número de documento"
                  />
                </div>
                
                {/* Banco */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Banco *
                  </label>
                  <select
                    name="datos_cobro.banco"
                    value={formData.datos_cobro.banco}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    <option value="">Seleccionar banco</option>
                    {BANCOS.map(banco => (
                      <option key={banco} value={banco}>{banco}</option>
                    ))}
                  </select>
                </div>
                
                {/* Tipo de cuenta */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tipo de Cuenta
                  </label>
                  <select
                    name="datos_cobro.tipo_cuenta"
                    value={formData.datos_cobro.tipo_cuenta}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                  >
                    {TIPOS_CUENTA.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Número de cuenta */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Número de Cuenta *
                  </label>
                  <input
                    type="text"
                    name="datos_cobro.numero_cuenta"
                    value={formData.datos_cobro.numero_cuenta}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Número de cuenta bancaria"
                  />
                </div>
                
                {/* Llave de pago */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Llave de Pago (Nequi/Daviplata)
                  </label>
                  <input
                    type="text"
                    name="datos_cobro.llave_pago"
                    value={formData.datos_cobro.llave_pago}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Número o llave de pago"
                  />
                </div>
                
                {/* Método de pago preferido */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Método de Pago Preferido
                  </label>
                  <input
                    type="text"
                    name="datos_cobro.metodo_pago"
                    value={formData.datos_cobro.metodo_pago}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Ej: Transferencia, Nequi"
                  />
                </div>
                
                {/* Teléfono de pago */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Teléfono Asociado al Pago
                  </label>
                  <input
                    type="tel"
                    name="datos_cobro.telefono_pago"
                    value={formData.datos_cobro.telefono_pago}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="Teléfono para notificaciones"
                  />
                </div>
                
                {/* Correo de pago */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Correo para Notificaciones de Pago
                  </label>
                  <input
                    type="email"
                    name="datos_cobro.correo_pago"
                    value={formData.datos_cobro.correo_pago}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Botón de guardar en móvil */}
          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 rounded-lg hover:bg-[#C49F27] disabled:opacity-50 transition-colors font-medium"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AliadoForm;
